import * as React from 'react';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { Num } from './index';
import Modal, { BotaoPrimario, BotaoSecundario, Campo, classesEntrada } from './Modal';
import { HistoricoGPS } from '../historicoGPS';
import { AutoCompleteUnico, buscarCrs } from './AutoComplete';

/* ============================================================================
   Editar dispositivo (nome, CPF e CR) - visual novo.

   AJUSTES DE 19/08/2026, a pedido:
     - a busca de CR so lista a partir de 2 caracteres digitados (antes despejava
       a lista inteira de CRs ao abrir);
     - um bloco de LEITURA com grupo e dados do aparelho;
     - o historico do dispositivo, recolhido, no proprio modal.
   O grupo NAO ganhou campo de edicao: trocar grupo reprovisiona o aparelho e tem
   tela propria.

   Substitui pages/dashboard/dispositivos/components/modal/index.tsx (`ModelCR`).

   Contrato, o mesmo de antes:
     PATCH /dispositivos/AlterarDispositivo { deviceId, cr, nome, cpf, dataCampo }
     GET   /crs/paginado?...campo=descricaoCompleta

   E o unico dos modais de dispositivo que grava em CADASTRO e nao manda comando
   para o aparelho - por isso veio antes dos tres de comando.

   `dataCampo` E OBRIGATORIO NA PRATICA: o backend grava dele a trilha de
   auditoria (nome, cpf e cr, sempre as tres entradas, mesmo sem mudanca - e
   assim que o modal antigo faz). Nao montar essa lista deixa a alteracao sem
   registro de quem mudou o que.

   O CPF E MASCARADO AQUI DENTRO. O modal antigo usava `RHFTextMask`, que depende
   do react-hook-form; como este nao usa RHF, a mascara e aplicada na digitacao e
   TAMBEM no valor que vem do banco - que chega sem pontuacao. Foi exatamente
   esse o bug corrigido em agosto no formulario de usuario: mascara que so
   formata o que se digita mostra o valor cru ao abrir o cadastro.
   ============================================================================ */

type Cr = { codigo?: string; descricao?: string; descricaoCompleta?: string };

type Device = {
  deviceId?: string;
  nome?: string;
  cpf?: string | number;
  nome_cr?: string;
  descricao_cr?: string;
  descricaoCompleta?: string;
  // Somente LEITURA, para a aba de detalhes. Ja vem na listagem - nenhuma chamada
  // nova para exibir isto.
  grupoNome?: string;
  grupoCodigo?: number;
  policyName?: string;
  hardwareInfo_manufacturer?: string;
  hardwareInfo_model?: string;
  hardwareInfo_serialNumber?: string;
  softwareInfo_androidVersion?: string;
  networkInfo_imei?: string;
  networkInfo_telephonyInfos_phoneNumber_1?: string;
  bateria?: number | string;
};

/** Idempotente: valor cru do banco ou ja formatado entram e saem iguais. */
function mascararCpf(valor: unknown) {
  const d = String(valor ?? '').replace(/\D/g, '').slice(0, 11);
  if (!d) return '';
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function Detalhe({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line py-1 last:border-0">
      <span className="flex-none text-[12px] font-semibold text-muted-foreground">{rotulo}</span>
      <span className="min-w-0 truncate text-right text-[12px]">{children}</span>
    </div>
  );
}

export default function ModalEditarDispositivo({
  aberto,
  device,
  onFechar,
  onSalvo,
}: {
  aberto: boolean;
  device: Device | null;
  onFechar: () => void;
  onSalvo?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const [nome, setNome] = React.useState('');
  const [cpf, setCpf] = React.useState('');
  const [cr, setCr] = React.useState<Cr | null>(null);
  const [salvando, setSalvando] = React.useState(false);

  React.useEffect(() => {
    if (!aberto || !device) return;
    setNome(device.nome ?? '');
    setCpf(mascararCpf(device.cpf));
    // O modal antigo remonta o CR a partir dos tres campos achatados que a
    // listagem devolve (nome_cr / descricao_cr / descricaoCompleta).
    setCr(
      device.nome_cr
        ? { codigo: device.nome_cr, descricao: device.descricao_cr, descricaoCompleta: device.descricaoCompleta }
        : null
    );
  }, [aberto, device]);

  const nomeOk = nome.trim().length > 0 && nome.trim().length <= 150;

  async function salvar() {
    if (!device?.deviceId || salvando || !nomeOk) return;
    setSalvando(true);
    try {
      // As TRES entradas sempre, com ou sem mudanca - mesmo formato do antigo.
      const dataCampo = JSON.stringify([
        { campo: 'nome', antigo: device.nome || ' - ', novo: nome || ' - ' },
        { campo: 'cpf', antigo: device.cpf || ' - ', novo: cpf || ' - ' },
        {
          campo: 'cr',
          antigo: device.descricaoCompleta || ' - ',
          novo: cr?.codigo ? `${cr.codigo} - ${cr.descricao}` : ' - ',
        },
      ]);

      await axios.patch('/dispositivos/AlterarDispositivo', {
        deviceId: device.deviceId,
        cr,
        nome,
        cpf,
        dataCampo,
      });

      enqueueSnackbar('Dispositivo foi salvo com sucesso.', { variant: 'success' });
      onSalvo?.();
      onFechar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'Ops ocorreu um erro no sistema.', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto={aberto}
      titulo="Editar dispositivo"
      descricao={device?.deviceId}
      icone="✎"
      largura="max-w-[720px]"
      onFechar={onFechar}
      rodape={
        <>
          <BotaoSecundario onClick={onFechar} disabled={salvando}>
            Cancelar
          </BotaoSecundario>
          <BotaoPrimario onClick={salvar} disabled={salvando || !nomeOk}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </BotaoPrimario>
        </>
      }
    >
      <Campo rotulo="Nome" obrigatorio>
        <input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          maxLength={150}
          autoFocus
          className={classesEntrada}
        />
      </Campo>

      <Campo rotulo="CPF" dica="Do responsável pelo aparelho.">
        <input
          value={cpf}
          onChange={(e) => setCpf(mascararCpf(e.target.value))}
          inputMode="numeric"
          placeholder="000.000.000-00"
          className={`${classesEntrada} tabular-nums`}
        />
      </Campo>

      {/* CR POR LUPA (21/10/2026). Antes a lista aparecia embaixo do campo com
          2+ caracteres e consultava a cada pausa na digitacao. Agora a lupa abre
          o seletor paginado (20 por pagina, com busca), o que tambem evita o
          paredao de CRs parecidos que levava a clicar no vizinho errado. */}
      <Campo bloco rotulo="CR" dica="Centro de resultado. Opcional.">
        <AutoCompleteUnico
          selecionado={
            cr
              ? { codigo: cr.codigo, rotulo: cr.descricaoCompleta || cr.descricao || '—', secundario: String(cr.codigo ?? '') }
              : null
          }
          aoEscolher={(o) => setCr(o ? o.dado : null)}
          buscar={buscarCrs}
          titulo="Selecionar CR"
          placeholder="Descrição do CR (Enter ou lupa)"
          vazio="Nenhum CR encontrado."
        />
      </Campo>

      {/* DETALHES E GRUPO - leitura.
          O grupo aparece aqui SEM campo para trocar, de proposito: mudar de grupo
          reprovisiona o aparelho (politica, apps, senha) e tem tela propria
          ("Mudar grupo", na mesma lista de acoes). Um seletor de grupo neste
          formulario, ao lado de nome e CPF, faria uma operacao de infraestrutura
          parecer edicao de cadastro. */}
      <div className="rounded-lg border border-line bg-surface-2 p-3">
        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
          Dispositivo
        </span>
        <Detalhe rotulo="Grupo">
          {device?.grupoNome || '—'}
          {device?.grupoCodigo ? <Num className="ml-1 text-faint">({device.grupoCodigo})</Num> : null}
        </Detalhe>
        <Detalhe rotulo="Aparelho">
          {[device?.hardwareInfo_manufacturer, device?.hardwareInfo_model].filter(Boolean).join(' ') || '—'}
        </Detalhe>
        <Detalhe rotulo="Android">{device?.softwareInfo_androidVersion || '—'}</Detalhe>
        <Detalhe rotulo="Serial">
          <Num>{device?.hardwareInfo_serialNumber || '—'}</Num>
        </Detalhe>
        <Detalhe rotulo="IMEI">
          <Num>{device?.networkInfo_imei || '—'}</Num>
        </Detalhe>
        <Detalhe rotulo="Telefone">
          <Num>{device?.networkInfo_telephonyInfos_phoneNumber_1 || '—'}</Num>
        </Detalhe>
        <p className="mt-2 text-[11px] text-faint">
          Para trocar o grupo use a ação <strong className="text-muted-foreground">Mudar grupo</strong> — ela
          reprovisiona o aparelho.
        </p>
      </div>

      {/* HISTORICO no proprio modal: a pergunta "quem mexeu neste aparelho" chega
          junto com "preciso corrigir o nome dele", e antes exigia fechar isto e
          abrir outra acao do menu. Fica depois dos campos e recolhido por padrao,
          para nao empurrar o formulario para baixo. */}
      {device?.deviceId && (
        <details className="rounded-lg border border-line">
          <summary className="cursor-pointer px-3 py-2 text-[12px] font-semibold text-muted-foreground hover:text-ink">
            Histórico deste dispositivo
          </summary>
          <div className="border-t border-line p-3">
            {/* O historico de dispositivo e gravado com chave = IMEI (e serial), nao
                deviceId - por isso esta aba vinha quase sempre vazia. Mesmas chaves
                que a ficha do aparelho usa. */}
            <HistoricoGPS
              tipo={'DISPOSITIVO'}
              chave={device.networkInfo_imei || device.deviceId}
              chaveSecundaria={device.hardwareInfo_serialNumber || ''}
            />
          </div>
        </details>
      )}
    </Modal>
  );
}
